import React, { useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import { EuiButton, EuiButtonEmpty, EuiFlexGroup, EuiFlexItem, EuiHealth, EuiHideFor, EuiImage, EuiInMemoryTable, EuiLoadingSpinner, EuiSpacer, EuiSwitch, EuiText, EuiToolTip, htmlIdGenerator } from '@elastic/eui'
import { EuiBasicTableColumn } from '@elastic/eui/src/components/basic_table/basic_table'

import { CampaignStatus, CreativeStatus, WhiteLabel } from 'api/interfaces'
import { useDeleteCreativeMutation } from 'api/rtkQueryApi/platform/creativesApi'
import { RootState } from 'app/rootReducer'
import { useEnterpriseSupport } from 'hooks/useEnterpriseSupport'
import LastCreativePausedErrorModal from 'features/campaigns/campaign/LastCreativePausedErrorModal'
import AgencyV2Modal from 'features/planner/components/AgencyV2Modal'
import history from 'services/HistoryService'
import { CreativeStatusToElasticColor, CreativeStatusToString } from 'utils/EnumToFriendly'
import { useWhiteLabel } from 'whiteLabel/WhiteLabelContext'

import { DEFAULT_INITIAL_PAGE_SIZE, DEFAULT_PAGE_SIZE_OPTIONS } from '../../../../utils/DefaultPageSize'
import { resetCreateAd } from '../../createAd/tv/retail/createAdTvRetailSlice'
import { AdHoverPreview } from '../AdHoverPreview'
import { AdState, fetchAds, pauseAd, startAd } from '../campaignSlice'

interface AdsDrawerProps {
  CreateAdButtonText?: string
  OnCreateAdClick?: () => void
}

const AdsDrawer = ({ CreateAdButtonText = 'Create Ad', OnCreateAdClick }: AdsDrawerProps) => {
  const dispatch = useDispatch()
  const whiteLabel = useWhiteLabel()
  const isEnterpriseOrganization = useEnterpriseSupport()
  const campaign = useSelector((state: RootState) => state.campaign)
  const { currentAccount } = useSelector((state: RootState) => state.app)
  const [currentPage, setCurrentPage] = useState(0)
  const [isLastCreativePausedErrorVisible, setIsLastCreativePausedErrorVisible] = useState(false)
  const [creativeToDelete, setCreativeToDelete] = useState<string>()
  const [deleteCreative, deleteCreativeResult] = useDeleteCreativeMutation()

  const onConfirmDeleteClick = async () => {
    if (!creativeToDelete) {
      return
    }

    const result = await deleteCreative({ accountId: currentAccount!.id, campaignId: campaign.campaign!.id, creativeId: creativeToDelete })

    if (result) {
      setCreativeToDelete(undefined)
      dispatch(fetchAds(currentAccount!.id, campaign.campaign!.id))
    }
  }

  const preview = (thumbnailUrl: string, name: string, sections: string[] | null, uploadedUrl: any) => (
    <EuiFlexGroup key={htmlIdGenerator()()} responsive={false} style={{ minWidth: 270 }} gutterSize='none'>
      <EuiHideFor sizes={['xs']}>
        <EuiFlexItem grow={false} style={{ width: 150, minWidth: 150 }} className='eui-alignMiddle'>
          <AdHoverPreview enabled={isEnterpriseOrganization} imageUrl={thumbnailUrl} videoUrl={uploadedUrl}>
            {uploadedUrl === null ? (
              <EuiImage alt='ad' url={thumbnailUrl || '/82x82.png'} size='fullWidth' />
            ) : (
              <video id='uploadedVideo' width='100%' preload='metadata' hidden={false}>
                <source src={uploadedUrl} type='video/mp4' />
              </video>
            )}
          </AdHoverPreview>
        </EuiFlexItem>
      </EuiHideFor>
      <EuiFlexItem grow={false} style={{ marginLeft: 10, width: 200 }}>
        <EuiText size='xs' style={{ textOverflow: 'ellipsis', overflow: 'hidden' }}>
          <strong>{name}</strong>
        </EuiText>
        <EuiText size='xs' color='subdued' style={{ textOverflow: 'ellipsis', overflow: 'hidden' }}>
          {sections && sections.map(s => <div key={htmlIdGenerator()()}>{s.replace('\\n', ' ')}</div>)}
        </EuiText>
      </EuiFlexItem>
    </EuiFlexGroup>
  )

  const columns: Array<EuiBasicTableColumn<AdState>> = [
    {
      width: '90px',
      name: 'ON/OFF',
      render: (ad: AdState) => (
        <React.Fragment>
          <EuiSwitch
            showLabel={false}
            label={''}
            checked={ad.checked}
            disabled={ad.disabled}
            onChange={e => {
              if (e.target.checked) {
                dispatch(startAd(currentAccount!.id, campaign!.campaign!.id, ad.id))
              } else {
                if (campaign!.adStates.filter(a => a.checked).length <= 1) {
                  setIsLastCreativePausedErrorVisible(true)
                  return
                }

                dispatch(pauseAd(currentAccount!.id, campaign!.campaign!.id, ad.id))
              }
            }}
          />
          {ad.loading && <EuiLoadingSpinner style={{ marginLeft: 5 }} />}
        </React.Fragment>
      )
    },
    {
      width: '240px',
      name: 'Ad',
      render: (ad: AdState) => preview(ad.thumbnailUrl, ad.name, ad.sections, ad.uploadedUrl)
    },
    {
      width: '110px',
      name: 'Status',
      render: (creative: AdState) => {
        let color = CreativeStatusToElasticColor(creative.status)
        let label = CreativeStatusToString(creative.status)

        return creative.status === CreativeStatus.Disapproved ? (
          <EuiToolTip
            key={htmlIdGenerator()()}
            position='right'
            content={
              <EuiText>
                <ul>
                  {creative.rejectionReasons.map(a => (
                    <li>{a}</li>
                  ))}
                </ul>
              </EuiText>
            }>
            <EuiHealth color={color}>{label}</EuiHealth>
          </EuiToolTip>
        ) : (
          <EuiHealth key={htmlIdGenerator()()} color={color}>
            {label}
          </EuiHealth>
        )
      }
    },
    {
      width: '215px',
      name: '',
      render: (ad: AdState) => (
        <React.Fragment key={htmlIdGenerator()()}>
          <EuiButtonEmpty iconType='inspect' onClick={() => history.push(`/campaigns/ads/${campaign.campaign!.id}/edit/${ad.id}`)} size='s'>
            View
          </EuiButtonEmpty>
        </React.Fragment>
      )
    },
    {
      name: 'Actions',
      actions: [
        {
          name: 'Delete',
          description: 'Delete',
          icon: 'trash',
          type: 'icon',
          color: 'danger',
          onClick: (item: AdState) => {
            setCreativeToDelete(item.id)
          },
          enabled: (item: AdState) => (campaign.campaign!.status === CampaignStatus.SavedForLater || campaign.campaign!.status === CampaignStatus.Disapproved) && whiteLabel?.getWhiteLabel() === WhiteLabel.AgencyV2,
          available: () => !deleteCreativeResult.isLoading || !campaign.isLoadingAds
        }
      ]
    }
  ]

  const pagination = {
    initialPageSize: DEFAULT_INITIAL_PAGE_SIZE,
    pageSizeOptions: DEFAULT_PAGE_SIZE_OPTIONS,
    pageIndex: currentPage
  }

  const createAd = () => {
    dispatch(resetCreateAd())
    OnCreateAdClick?.()
  }

  return (
    <React.Fragment>
      <EuiSpacer />
      <EuiFlexGroup alignItems='center'>
        <EuiFlexItem />
        <EuiFlexItem grow={false}>
          <EuiButton id='create' size='s' key='createAd' onClick={createAd} fill>
            {CreateAdButtonText}
          </EuiButton>
        </EuiFlexItem>
      </EuiFlexGroup>
      <EuiInMemoryTable
        loading={campaign.isLoadingAds}
        items={campaign.adStates}
        columns={columns}
        pagination={pagination}
        responsive={false}
        onChange={(criteria: any) => {
          setCurrentPage(criteria.page.index)
        }}
      />
      <LastCreativePausedErrorModal isModalVisible={isLastCreativePausedErrorVisible} closeModal={() => setIsLastCreativePausedErrorVisible(false)} />
      {creativeToDelete && (
        <AgencyV2Modal isLoading={deleteCreativeResult.isLoading || campaign.isLoadingAds} title='Remove Commercial' onClose={() => setCreativeToDelete(undefined)} confirmButtonText='Remove' onConfirmButtonClick={onConfirmDeleteClick} showCancelButton>
          <EuiText>This commercial will be removed from the campaign.</EuiText>
        </AgencyV2Modal>
      )}
    </React.Fragment>
  )
}

export default AdsDrawer
